'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

type LoginForm = { email: string; password: string; remember: boolean };
type MFAForm = { code: string };

type RequestAccessForm = {
  firstName: string;
  lastName: string;
  firmName: string;
  workEmail: string;
  jobTitle: string;
  rationale: string;
};

const PERSONAL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com',
  'outlook.co.uk', 'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'me.com',
  'mac.com', 'live.com', 'live.co.uk', 'msn.com', 'aol.com', 'protonmail.com',
  'proton.me', 'tutanota.com', 'zoho.com', 'yandex.com', 'mail.com',
];

function isPersonalEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  return PERSONAL_DOMAINS.includes(domain);
}

const DEMO_ACCOUNTS = [
  { role: 'Investment User', email: 'j.rothbury@meridiancap.com', password: 'Nasca2026' },
  { role: 'Firm Admin', email: 'admin@stonebridgepartners.co.uk', password: 'NascaAdmin2026' },
  { role: 'Platform Admin', email: 'ops@nasca.capital', password: 'NascaOps2026' },
];

export default function LoginClient() {
  const [mode, setMode] = useState<'login' | 'request-access' | 'mfa' | 'forgot' | 'submitted'>('login');
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedStatus, setSubmittedStatus] = useState<'pending_platform_review' | 'pending_firm_approval'>('pending_platform_review');
  const [loggedInEmail, setLoggedInEmail] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const mfaForm = useForm<MFAForm>({ defaultValues: { code: '' } });
  const requestForm = useForm<RequestAccessForm>();

  const onLoginSubmit = async (data: LoginForm) => {
    setLoginError('');
    setIsLoading(true);
    setTimeout(() => {
      const valid = DEMO_ACCOUNTS.find(
        (a) => a.email === data.email && a.password === data.password
      );
      if (!valid) {
        setLoginError('Invalid credentials. Please check your email and password.');
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      setLoggedInEmail(data.email);
      setMode('mfa');
    }, 600);
  };

  const onMFASubmit = (data: MFAForm) => {
    setMfaError('');
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      if (data.code === '123456') {
        const account = DEMO_ACCOUNTS.find((a) => a.email === loggedInEmail);
        if (account?.role === 'Investment User') {
          window.location.href = '/investment-dashboard';
        } else if (account?.role === 'Firm Admin') {
          window.location.href = '/firm-dashboard';
        } else {
          window.location.href = '/deal-feed';
        }
      } else {
        setMfaError('Incorrect verification code. Please try again.');
      }
    }, 700);
  };

  const onRequestAccessSubmit = async (data: RequestAccessForm) => {
    setIsLoading(true);
    setTimeout(() => {
      const domain = data.workEmail.split('@')[1]?.toLowerCase() ?? '';
      const VERIFIED_DOMAINS = ['stonebridgepartners.co.uk', 'meridiancap.com'];
      const requestStatus: 'pending_platform_review' | 'pending_firm_approval' = VERIFIED_DOMAINS.includes(domain)
        ? 'pending_firm_approval' :'pending_platform_review';
      setSubmittedEmail(data.workEmail);
      setSubmittedStatus(requestStatus);
      setIsLoading(false);
      setMode('submitted');
    }, 800);
  };

  const fillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--primary)' }}>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] shrink-0 p-10 relative overflow-hidden"
        style={{ backgroundColor: 'var(--secondary)' }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 opacity-5" style={{ background: 'radial-gradient(circle at top right, var(--gold), transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 opacity-5" style={{ background: 'radial-gradient(circle at bottom left, var(--gold), transparent 70%)' }} />

        <div>
          <div className="flex items-center gap-3">
            <AppLogo width={130} height={46} />
          </div>
        </div>

        <div>
          <div className="w-8 h-px mb-8" style={{ backgroundColor: 'var(--gold)' }} />
          <h1 className="text-white text-2xl font-light leading-relaxed mb-4" style={{ letterSpacing: '-0.01em' }}>
            Controlled access.
            <br />
            <span style={{ color: 'var(--gold)' }}>Verified counterparties.</span>
            <br />
            Off-market deals.
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(174,184,196,0.75)' }}>
            A permissioned network for institutional deal interaction. Access is controlled,
            every engagement is logged, and every fee is protected.
          </p>
        </div>

        <div className="space-y-3">
          {[
            { icon: 'ShieldCheckIcon', text: 'KYC-verified counterparties only' },
            { icon: 'LockClosedIcon', text: 'Master Fee Agreement binding on every request' },
            { icon: 'DocumentCheckIcon', text: 'Full audit trail — deal ID, firm, timestamp' },
          ].map((item) => (
            <div key={`trust-${item.text}`} className="flex items-center gap-3">
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} variant="outline" className="shrink-0" style={{ color: 'var(--gold)' }} />
              <span className="text-xs" style={{ color: 'rgba(174,184,196,0.65)' }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <AppLogo width={130} height={46} />
          </div>

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <div>
              <h2 className="text-white text-xl font-semibold mb-1">Sign in</h2>
              <p className="text-sm mb-7" style={{ color: 'rgba(174,184,196,0.7)' }}>
                Access is restricted to verified institutional members.
              </p>

              <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} noValidate>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="login-email" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>EMAIL ADDRESS</label>
                    <input
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      className="nasca-input"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: loginForm.formState.errors.email ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                      placeholder="you@firm.com"
                      {...loginForm.register('email', {
                        required: 'Email address is required.',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
                      })}
                    />
                    {loginForm.formState.errors.email && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{loginForm.formState.errors.email.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="login-password" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>PASSWORD</label>
                    <div className="relative">
                      <input
                        id="login-password"
                        type={showPass ? 'text' : 'password'}
                        autoComplete="current-password"
                        className="nasca-input pr-10"
                        style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: loginForm.formState.errors.password ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                        placeholder="••••••••"
                        {...loginForm.register('password', {
                          required: 'Password is required.',
                          minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                        })}
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors" onClick={() => setShowPass(!showPass)} aria-label={showPass ? 'Hide password' : 'Show password'}>
                        <Icon name={showPass ? 'EyeSlashIcon' : 'EyeIcon'} size={15} variant="outline" />
                      </button>
                    </div>
                    {loginForm.formState.errors.password && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{loginForm.formState.errors.password.message}</p>}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-3.5 h-3.5 rounded-sm" style={{ accentColor: 'var(--gold)' }} {...loginForm.register('remember')} />
                      <span className="text-xs" style={{ color: 'rgba(174,184,196,0.7)' }}>Remember this device</span>
                    </label>
                    <button type="button" onClick={() => setMode('forgot')} className="text-xs transition-colors" style={{ color: 'var(--gold)' }}>Forgot password?</button>
                  </div>

                  {loginError && (
                    <div className="flex items-start gap-2 p-3 rounded text-xs" style={{ backgroundColor: 'rgba(139, 26, 26, 0.15)', border: '1px solid rgba(139,26,26,0.35)', color: '#F87171' }}>
                      <Icon name="ExclamationCircleIcon" size={14} variant="outline" className="shrink-0 mt-0.5" />
                      <span>{loginError}</span>
                    </div>
                  )}

                  <button type="submit" disabled={isLoading} className="nasca-btn-accent w-full h-10 disabled:opacity-60 disabled:cursor-not-allowed">
                    {isLoading ? <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" /> : 'Sign In'}
                  </button>
                </div>
              </form>

              <div className="mt-4 flex items-start gap-2 p-3 rounded text-xs" style={{ backgroundColor: 'rgba(185,154,91,0.1)', border: '1px solid rgba(185,154,91,0.25)', color: 'rgba(185,154,91,0.9)' }}>
                <Icon name="InformationCircleIcon" size={14} variant="outline" className="shrink-0 mt-0.5" />
                <span>Your email domain must match your firm&apos;s registered domain. Contact your Firm Administrator if access is denied.</span>
              </div>

              <p className="mt-5 text-xs text-center" style={{ color: 'rgba(174,184,196,0.5)' }}>
                Not a member?{' '}
                <button type="button" onClick={() => setMode('request-access')} className="transition-colors" style={{ color: 'var(--gold)' }}>
                  Request access
                </button>
              </p>
            </div>
          )}

          {/* ── MFA ── */}
          {mode === 'mfa' && (
            <div>
              <button type="button" onClick={() => setMode('login')} className="flex items-center gap-1.5 text-xs mb-6 transition-colors" style={{ color: 'rgba(174,184,196,0.7)' }}>
                <Icon name="ArrowLeftIcon" size={13} variant="outline" />
                Back to sign in
              </button>
              <div className="w-10 h-10 rounded flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(185,154,91,0.15)' }}>
                <Icon name="ShieldCheckIcon" size={20} variant="outline" style={{ color: 'var(--gold)' }} />
              </div>
              <h2 className="text-white text-xl font-semibold mb-1">Two-factor authentication</h2>
              <p className="text-sm mb-7" style={{ color: 'rgba(174,184,196,0.7)' }}>Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={mfaForm.handleSubmit(onMFASubmit)} noValidate>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="mfa-code" className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>VERIFICATION CODE</label>
                    <input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="nasca-input text-center text-lg tracking-widest font-tabular"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: mfaError ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white', letterSpacing: '0.35em' }}
                      placeholder="000000"
                      {...mfaForm.register('code', {
                        required: 'Verification code is required.',
                        pattern: { value: /^\d{6}$/, message: 'Enter the 6-digit code.' },
                      })}
                    />
                    {mfaForm.formState.errors.code && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{mfaForm.formState.errors.code.message}</p>}
                    {mfaError && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{mfaError}</p>}
                  </div>
                  <div className="text-xs p-3 rounded" style={{ backgroundColor: 'rgba(185,154,91,0.08)', border: '1px solid rgba(185,154,91,0.2)', color: 'rgba(174,184,196,0.6)' }}>
                    Demo code: <span className="font-semibold" style={{ color: 'var(--gold)' }}>123456</span>
                  </div>
                  <button type="submit" disabled={isLoading} className="nasca-btn-accent w-full h-10 disabled:opacity-60">
                    {isLoading ? <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" /> : 'Verify Identity'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {mode === 'forgot' && (
            <div>
              <button type="button" onClick={() => setMode('login')} className="flex items-center gap-1.5 text-xs mb-6 transition-colors" style={{ color: 'rgba(174,184,196,0.7)' }}>
                <Icon name="ArrowLeftIcon" size={13} variant="outline" />
                Back to sign in
              </button>
              <h2 className="text-white text-xl font-semibold mb-1">Reset password</h2>
              <p className="text-sm mb-7" style={{ color: 'rgba(174,184,196,0.7)' }}>Enter your registered email address. A reset link will be sent if the account exists.</p>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>EMAIL ADDRESS</label>
                <input type="email" className="nasca-input" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }} placeholder="you@firm.com" />
              </div>
              <button type="button" className="nasca-btn-accent w-full h-10 mt-4" onClick={() => setMode('login')}>Send Reset Link</button>
            </div>
          )}

          {/* ── REQUEST ACCESS FORM ── */}
          {mode === 'request-access' && (
            <div>
              <button type="button" onClick={() => setMode('login')} className="flex items-center gap-1.5 text-xs mb-5 transition-colors" style={{ color: 'rgba(174,184,196,0.7)' }}>
                <Icon name="ArrowLeftIcon" size={13} variant="outline" />
                Back to sign in
              </button>
              <h2 className="text-white text-xl font-semibold mb-1">Request access</h2>
              <p className="text-sm mb-5" style={{ color: 'rgba(174,184,196,0.7)' }}>
                Membership is subject to KYC verification and approval. Use your institutional work email.
              </p>

              <form onSubmit={requestForm.handleSubmit(onRequestAccessSubmit)} noValidate className="space-y-3">
                {/* Name */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>FIRST NAME <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                    <input
                      type="text"
                      className="nasca-input"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.firstName ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                      placeholder="James"
                      {...requestForm.register('firstName', { required: 'Required' })}
                    />
                    {requestForm.formState.errors.firstName && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.firstName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>LAST NAME <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                    <input
                      type="text"
                      className="nasca-input"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.lastName ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                      placeholder="Rothbury"
                      {...requestForm.register('lastName', { required: 'Required' })}
                    />
                    {requestForm.formState.errors.lastName && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.lastName.message}</p>}
                  </div>
                </div>

                {/* Firm name */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>FIRM NAME <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                  <input
                    type="text"
                    className="nasca-input"
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.firmName ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                    placeholder="Meridian Capital LP"
                    {...requestForm.register('firmName', { required: 'Firm name is required.' })}
                  />
                  {requestForm.formState.errors.firmName && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.firmName.message}</p>}
                </div>

                {/* Work email */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>WORK EMAIL <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                  <input
                    type="email"
                    className="nasca-input"
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.workEmail ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                    placeholder="you@firm.com"
                    {...requestForm.register('workEmail', {
                      required: 'Work email is required.',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
                      validate: (v) => !isPersonalEmail(v) || 'Personal email addresses are not accepted. Please use your institutional work email.',
                    })}
                  />
                  {requestForm.formState.errors.workEmail && (
                    <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.workEmail.message}</p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>ROLE / TITLE <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                  <input
                    type="text"
                    className="nasca-input"
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.jobTitle ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white' }}
                    placeholder="e.g. Managing Director"
                    {...requestForm.register('jobTitle', { required: 'Role is required.' })}
                  />
                  {requestForm.formState.errors.jobTitle && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.jobTitle.message}</p>}
                </div>

                {/* Rationale */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.04em' }}>REASON FOR ACCESS <span style={{ color: 'var(--status-denied)' }}>*</span></label>
                  <textarea
                    className="nasca-input"
                    rows={3}
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: requestForm.formState.errors.rationale ? 'var(--status-denied)' : 'rgba(255,255,255,0.12)', color: 'white', resize: 'none' }}
                    placeholder="Briefly describe your investment mandate and why you are seeking access to Nasca Capital..."
                    {...requestForm.register('rationale', { required: 'Please provide a reason for access.', minLength: { value: 30, message: 'Please provide at least 30 characters.' } })}
                  />
                  {requestForm.formState.errors.rationale && <p className="text-xs mt-1" style={{ color: 'var(--status-denied)' }}>{requestForm.formState.errors.rationale.message}</p>}
                </div>

                {/* Personal email warning */}
                <div className="flex items-start gap-2 p-3 rounded text-xs" style={{ backgroundColor: 'rgba(185,154,91,0.08)', border: '1px solid rgba(185,154,91,0.2)', color: 'rgba(185,154,91,0.85)' }}>
                  <Icon name="ShieldCheckIcon" size={13} variant="outline" className="shrink-0 mt-0.5" />
                  <span>Nasca Capital is an institutional platform. Personal email addresses (Gmail, Hotmail, Yahoo etc.) are not accepted.</span>
                </div>

                <button type="submit" disabled={isLoading} className="nasca-btn-accent w-full h-10 mt-1 disabled:opacity-60">
                  {isLoading ? <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" /> : 'Submit Access Request'}
                </button>
              </form>

              <p className="mt-4 text-xs text-center" style={{ color: 'rgba(174,184,196,0.45)' }}>
                By submitting, you agree to Nasca Capital&apos;s{' '}
                <span className="underline cursor-pointer" style={{ color: 'var(--gold)' }}>Terms of Access</span>{' '}
                and{' '}
                <span className="underline cursor-pointer" style={{ color: 'var(--gold)' }}>Privacy Policy</span>.
              </p>
            </div>
          )}

          {/* ── SUBMITTED CONFIRMATION ── */}
          {mode === 'submitted' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(185,154,91,0.15)' }}>
                <Icon name="CheckCircleIcon" size={28} variant="outline" style={{ color: 'var(--gold)' }} />
              </div>
              <h2 className="text-white text-xl font-semibold mb-2">Request submitted</h2>
              <p className="text-sm mb-6" style={{ color: 'rgba(174,184,196,0.7)' }}>
                Your access request has been received. You will be notified at{' '}
                <span className="font-medium" style={{ color: 'rgba(174,184,196,0.9)' }}>{submittedEmail}</span>{' '}
                once a decision has been made.
              </p>

              <div className="text-left p-4 rounded mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'rgba(174,184,196,0.85)', letterSpacing: '0.06em' }}>WHAT HAPPENS NEXT</p>
                {submittedStatus === 'pending_firm_approval' ? (
                  <div className="space-y-2.5">
                    {[
                      'Your email domain matches an existing firm on the platform.',
                      'The Firm Administrator has been notified and will review your request.',
                      'You will receive an email once approved or if further information is needed.',
                      'Upon approval, you will receive login credentials and onboarding instructions.',
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5" style={{ backgroundColor: 'var(--gold)', color: 'var(--primary)', fontSize: '9px' }}>{i + 1}</span>
                        <p className="text-xs" style={{ color: 'rgba(174,184,196,0.7)' }}>{step}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {[
                      'Your request is under review by the Nasca Capital team.',
                      'We may contact you for additional information about your firm.',
                      'If approved, you will receive an invite to complete firm onboarding.',
                      'Onboarding includes KYC verification, MFA setup, and fee agreement.',
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5" style={{ backgroundColor: 'var(--gold)', color: 'var(--primary)', fontSize: '9px' }}>{i + 1}</span>
                        <p className="text-xs" style={{ color: 'rgba(174,184,196,0.7)' }}>{step}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="button" onClick={() => { setMode('login'); requestForm.reset(); }} className="text-xs" style={{ color: 'var(--gold)' }}>
                ← Return to sign in
              </button>
            </div>
          )}

          {/* Demo credentials */}
          {(mode === 'login' || mode === 'mfa') && (
            <div className="mt-6 p-4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs font-semibold mb-3" style={{ color: 'rgba(174,184,196,0.6)', letterSpacing: '0.08em' }}>DEMO ACCOUNTS</p>
              <div className="space-y-2">
                {DEMO_ACCOUNTS.map((acct) => (
                  <div key={`demo-${acct.role}`} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-white truncate">{acct.role}</p>
                      <p className="text-xs truncate" style={{ color: 'rgba(174,184,196,0.55)', fontSize: '11px' }}>{acct.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fillCredentials(acct.email, acct.password)}
                      className="shrink-0 text-xs px-2.5 py-1 rounded transition-colors"
                      style={{ backgroundColor: 'rgba(185,154,91,0.12)', border: '1px solid rgba(185,154,91,0.25)', color: 'var(--gold)' }}
                    >
                      Use
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}